





















































/**
 * External dependencies.
 */
import Component, { mixins } from 'vue-class-component'
import { Prop, Provide, Watch } from 'vue-property-decorator'
import { SlideYUpTransition } from 'vue2-transitions'

/**
 * Internal dependencies.
 */
import { lazyInject } from '@/container'
import { TYPES } from '@/types'
import PhotosHelperMixin from '@/mixins/PhotosHelperMixin.vue'
import UserInfo from '@/features/account/components/UserInfo.vue'
import ProfileDefault from '@/components/account/ProfileDefault.vue'
import ProfileSettings from '@/components/account/ProfileSettings.vue'
import ProfileUpload from '@/components/account/ProfileUpload.vue'
import ProfilePhotos from '@/components/account/ProfilePhotos.vue'
import ProfileSubNav from '@/components/account/ProfileSubNav.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'

import { namespace as authenticationModule } from '@/store/authentication'
import { namespace as commonModule } from '@/store/common'
import { namespace as profileModule } from '@/store/profile'
import { RESET_PROFILE_DATA } from '@/store/profile/constants'

import { UserData } from '@/store/authentication/interfaces/UserData'
import { Modes } from '@/store/common/enum/Modes'
import { Route } from 'vue-router'

import { scrollTo } from '@/utils/helpers'

let isReturningFromAccountPhoto = false

Component.registerHooks([
  'beforeRouteEnter',
  'beforeRouteUpdate'
])

@Component({
  components: {
    UserInfo,
    ProfileDefault,
    ProfileUpload,
    ProfileSettings,
    ProfilePhotos,
    ProfileSubNav,
    LoadingSpinner,
    SlideYUpTransition
  },
  provide () {
    return {
      swapToDefaultComponent: (this as any).swapToDefaultComponent
    }
  }
})
export default class AccountProfile extends mixins(PhotosHelperMixin) {
  @Prop({ type: String, required: false }) component: string

  @lazyInject(TYPES.PhotoService)
  PhotoService: any

  @lazyInject(TYPES.UserService)
  UserService: any

  photographer: object | Array<any> | any = {}
  userId: string | any = null
  currentProfileComponent: string = this.currentDefault
  showSettings: boolean = false
  loadingData: boolean = false
  autoload: boolean = true

  @authenticationModule.Getter isLoggedIn: boolean
  @authenticationModule.Getter getUserData: UserData

  @commonModule.Getter getMode: Modes

  get isProfileCurrentUser (): boolean {
    return this.isLoggedIn && this.userId === this.getUserData.id
  }

  get getPhotographer (): UserData | null {
    if (this.isProfileCurrentUser) {
      return this.getUserData
    }

    return this.photographer
  }

  get defaultIsUpload () {
    return this.getMode === Modes.OPEN
  }

  get currentDefault () {
    if (this.isProfileCurrentUser) {
      return this.defaultIsUpload ? 'ProfileUpload' : 'ProfilePhotos'
    }

    return 'ProfileDefault'
  }

  get profileCompleted () {
    if (!this.isProfileCurrentUser) {
      return true
    }

    return !!this.getPhotographer!.profile_completed_at
  }

  created () {
    this.autoload = !isReturningFromAccountPhoto

    void this.handleUserDisplayData(this.$route.params.id)
      .finally(() => {
        this.loadingData = false

        this.showSettings = !this.profileCompleted
      })

    this.updateFromRouteProps()
  }

  beforeMount () {
    this.currentProfileComponent = this.currentDefault
  }

  beforeRouteEnter (to: Route, from: Route, next: () => void) {
    if (from && from.name === 'accountPhoto') {
      isReturningFromAccountPhoto = true
    }

    next()
  }

  beforeRouteUpdate (to: Route, from: Route, next: () => void) {
    void this.handleUserDisplayData(to.params.id)
      .finally(() => this.loadingData = false)

    next()
  }

  @profileModule.Action(RESET_PROFILE_DATA) resetProfileData: () => void

  handleUserDisplayData (userId: string): Promise<void> {
    this.resetProfileData()

    this.userId = userId

    if (this.isProfileCurrentUser) {
      this.currentProfileComponent = this.currentDefault

      return Promise.resolve()
    }

    this.loadingData = true

    return this.UserService.getUser(this.userId)
      .then((response: any) => {
        this.photographer = response.data
        this.currentProfileComponent = this.currentDefault
      })
  }

  // used to load component when passing query from outside
  updateFromRouteProps () {
    // this.component from route props: params: { id: this.user.id, component: 'photos' }
    switch (this.component) {
      case 'photos':
        return this.swapComponent('ProfilePhotos')
        break
      case 'upload':
        return this.swapComponent('ProfileUpload')
        break
    }
  }

  async swapComponent (component: string | { component: string, shouldScroll: boolean }) {
    if (!this.profileCompleted) {
      return
    }

    if (!this.isLoggedIn) {
      void this.$router.push({
        name: 'login'
      })

      return
    }

    if (!this.isProfileCurrentUser) {
      void this.$router.push({
        name: 'profile',
        params: {
          id: this.getUserData.id
        }
      })

      return
    }

    this.showSettings = false

    let shouldScroll = true

    if (typeof component === 'string') {
      this.currentProfileComponent = component
    } else {
      this.currentProfileComponent = component.component
      shouldScroll = component.shouldScroll
    }

    // this.$router.replace({ name: 'profile', params: { id: this.getUserData.id } })
    // .catch((error: any) => {
    //   console.log(error)
    // })

    await this.$nextTick()

    const subNav = document.querySelector('.subnav') as HTMLElement
    shouldScroll && scrollTo(subNav)
  }

  toggleSettings () {
    if (!this.profileCompleted) {
      return
    }

    this.showSettings = !this.showSettings
  }

  closeSettings () {
    const element = document.querySelector('.profile-header') as HTMLElement
    this.toggleSettings()
    setTimeout(() => {
      scrollTo(element, 1200)
    }, 600)
  }

  scrollToSubNav () {
    if ('$el' in this.$refs.profileSubNav) {
      scrollTo(this.$refs.profileSubNav.$el)
    }
  }

  swapToDefaultComponent () {
    void this.swapComponent(this.currentDefault)
  }
}
